import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Presentation, PlayCircle, Radio } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { hi } from "@/lib/classroom_hi";
import { downloadNotes } from "@/lib/classroom_live";

const INK = ["#0A192F", "#C81E4B", "#00707F"];
const Tool = ({ onClick, disabled, label, testid, children }) => (
  <button onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testid}
    className="h-9 min-w-9 px-2.5 rounded-full border border-slate-300 bg-white text-[#0A192F] text-xs font-bold inline-flex items-center justify-center gap-1 hover:border-[#00B8D4] disabled:opacity-35 transition-colors">{children}</button>
);

// Chapter whiteboard: follows the lesson live; students can flip back through earlier notes or download them all.
export const Whiteboard = ({ lesson, cards, live, onSeek }) => {
  const { L, lang } = useLang();
  const [pick, setPick] = useState(null);
  useEffect(() => setPick(null), [lesson.slug]);
  const i = Math.min(pick ?? live, cards.length - 1);
  const c = cards[i];
  if (!c) return null;
  const go = (d) => setPick(Math.min(cards.length - 1, Math.max(0, i + d)));
  return (
    <section className="mt-6 rounded-3xl bg-white border-[6px] border-[#CBD5E1] shadow-[0_24px_60px_-30px_rgba(10,25,47,0.45)] p-5 sm:p-7" data-testid="whiteboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#007A8A]">
          <Presentation className="w-4 h-4" />{L("Whiteboard notes", "व्हाइटबोर्ड नोट्स")}
          <span className="text-slate-500 normal-case tracking-normal font-semibold" data-testid="whiteboard-count">· {i + 1}/{cards.length}</span>
          {pick === null && <span className="inline-flex items-center gap-1 text-[#C81E4B] normal-case tracking-normal"><Radio className="w-3 h-3" />{L("live", "लाइव")}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <Tool onClick={() => go(-1)} disabled={i === 0} label={L("Previous notes", "पिछले नोट्स")} testid="whiteboard-prev-btn"><ChevronLeft className="w-4 h-4" /></Tool>
          <Tool onClick={() => go(1)} disabled={i >= cards.length - 1} label={L("Next notes", "अगले नोट्स")} testid="whiteboard-next-btn"><ChevronRight className="w-4 h-4" /></Tool>
          {pick !== null && <Tool onClick={() => { onSeek(i); setPick(null); }} label={L("Play this chapter", "यह चैप्टर चलाएँ")} testid="whiteboard-seek-btn"><PlayCircle className="w-4 h-4" />{L("Play", "चलाएँ")}</Tool>}
          {pick !== null && <Tool onClick={() => setPick(null)} label={L("Follow the class", "क्लास के साथ चलें")} testid="whiteboard-follow-btn">{L("Live", "लाइव")}</Tool>}
          <Tool onClick={() => downloadNotes(lesson, cards)} label={L("Download all notes", "सारे नोट्स डाउनलोड करें")} testid="whiteboard-download-btn"><Download className="w-4 h-4" /></Tool>
        </div>
      </div>
      <div key={`${lesson.slug}-${i}`} className="mt-4 min-h-[150px]">
        <h3 className="rs-marker rs-ink-in text-2xl sm:text-3xl font-bold text-[#0A192F] leading-tight" data-testid="whiteboard-title">{lang === "hi" ? hi(c.title) : c.title}</h3>
        <ul className="mt-3 space-y-1.5">
          {c.points.map((p, k) => (
            <li key={k} className="rs-marker rs-ink-in text-lg sm:text-xl leading-snug flex gap-3" style={{ animationDelay: `${0.25 + k * 0.3}s`, color: INK[k % 3] }} data-testid={`whiteboard-point-${k}`}>
              <span aria-hidden>•</span><span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <div aria-hidden className="mt-5 flex justify-end gap-2">{INK.map((ink) => <span key={ink} className="w-10 h-2.5 rounded-full" style={{ background: ink }} />)}</div>
    </section>
  );
};
